import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { trackEvent } from "../../utils/tracking";
import "./BookRepair.css";

// Business hours reference (for slot generation)
const HOURS_BY_DAY = {
  0: { open: 10, close: 20 }, // Sunday
  1: { open: 10, close: 20 }, // Monday
  2: { open: 10, close: 20 }, // Tuesday
  3: { open: 10, close: 20 }, // Wednesday
  4: { open: 10, close: 20 }, // Thursday
  5: { open: 10, close: 21 }, // Friday
  6: { open: 10, close: 21 }, // Saturday
};

// Format date as YYYY-MM-DD
const fmtDate = (d) => d.toISOString().split("T")[0];

// Format date for display
const displayDate = (d) =>
  d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// Generate 7-day window of available dates (today + next 6 days)
const getAvailableDates = () => {
  const dates = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    dates.push(d);
  }
  return dates;
};

// Generate hour slots for a given date based on business hours
const getSlotsForDate = (date) => {
  const day = date.getDay();
  const hours = HOURS_BY_DAY[day];
  if (!hours) return [];

  const slots = [];
  const now = new Date();
  const isToday = fmtDate(date) === fmtDate(now);
  const currentHour = now.getHours();

  for (let h = hours.open; h < hours.close; h++) {
    // Skip slots that have already passed today
    if (isToday && h <= currentHour) continue;
    slots.push({
      value: `${h}:00`,
      label: `${h > 12 ? h - 12 : h}:00 ${h >= 12 ? "PM" : "AM"}`,
    });
    slots.push({
      value: `${h}:30`,
      label: `${h > 12 ? h - 12 : h}:30 ${h >= 12 ? "PM" : "AM"}`,
    });
  }
  return slots;
};

const BookRepair = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1: device+issue, 2: date+time, 3: contact+confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [device, setDevice] = useState("");
  const [issue, setIssue] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const formRef = useRef();
  const availableDates = getAvailableDates();
  const slots = selectedDate ? getSlotsForDate(selectedDate) : [];

  // Fire tracking when widget opens
  useEffect(() => {
    trackEvent.bookingStart();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    const formData = new FormData(formRef.current);
    const name = formData.get("name");
    const email = formData.get("email");
    const phone = formData.get("phone");

    const bookingSummary = `NEW BOOKING REQUEST
Device: ${device}
Issue: ${issue}
Requested date: ${displayDate(selectedDate)}
Requested time: ${slots.find((s) => s.value === selectedSlot)?.label || selectedSlot}
Name: ${name}
Phone: ${phone}`;

    emailjs
      .send(
        "service_f4grfuj",
        "template_bjg4ob4",
        {
          name: name,
          email: email,
          reply_to: email,
          device: device,
          subject: `Booking Request: ${device} - ${issue}`,
          message: bookingSummary,
        },
        { publicKey: "LyFrQSP0J8xcnoKSH" }
      )
      .then(
        () => {
          trackEvent.bookingComplete({ device, service: issue });
          setStatus({
            type: "success",
            message:
              "Booking request received! We'll confirm your slot within 1 hour via phone or email.",
          });
          setSubmitting(false);
        },
        (err) => {
          console.log("Booking error:", err);
          setStatus({
            type: "error",
            message:
              "Couldn't submit booking. Please call (215) 939-3933 directly.",
          });
          setSubmitting(false);
        }
      );
  };

  return (
    <div className="book-repair-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="book-repair-modal">
        <button className="book-repair-close" onClick={onClose} aria-label="Close booking">
          &times;
        </button>

        {/* Header */}
        <div className="book-repair-header">
          <div className="book-repair-icon">
            <i className="fas fa-calendar-check" />
          </div>
          <h2 className="book-repair-title">Book Your Repair</h2>
          <p className="book-repair-subtitle">
            Reserve your same-day repair slot in under 60 seconds
          </p>
        </div>

        {/* Stepper */}
        <div className="book-repair-stepper">
          <div className={`step ${step >= 1 ? "active" : ""} ${step > 1 ? "done" : ""}`}>
            <span>1</span>
            <label>Device</label>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 2 ? "active" : ""} ${step > 2 ? "done" : ""}`}>
            <span>2</span>
            <label>Schedule</label>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 3 ? "active" : ""}`}>
            <span>3</span>
            <label>Confirm</label>
          </div>
        </div>

        {/* Body */}
        <div className="book-repair-body">
          {/* STEP 1: device + issue */}
          {step === 1 && (
            <div>
              <div className="form-group">
                <label>Your Device</label>
                <select
                  value={device}
                  onChange={(e) => setDevice(e.target.value)}
                  className="book-input"
                >
                  <option value="">Select device…</option>
                  <option value="iPhone">iPhone</option>
                  <option value="Samsung">Samsung</option>
                  <option value="iPad">iPad / Tablet</option>
                  <option value="MacBook">MacBook</option>
                  <option value="Laptop">Windows Laptop</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>What Needs Repair?</label>
                <select
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                  className="book-input"
                >
                  <option value="">Select issue…</option>
                  <option value="Screen Repair">Cracked / Broken Screen</option>
                  <option value="Battery Replacement">Battery Issue</option>
                  <option value="Charging Port Repair">Won't Charge</option>
                  <option value="Water Damage">Water Damage</option>
                  <option value="Data Recovery">Lost / Need Data</option>
                  <option value="Software Issue">Software / Slow</option>
                  <option value="Other">Something Else</option>
                </select>
              </div>
              <button
                className="book-btn book-btn-primary"
                disabled={!device || !issue}
                onClick={() => setStep(2)}
              >
                Next: Pick Date & Time
                <i className="fas fa-arrow-right ml-2" />
              </button>
            </div>
          )}

          {/* STEP 2: date + time */}
          {step === 2 && (
            <div>
              <div className="form-group">
                <label>Pick a Date</label>
                <div className="date-grid">
                  {availableDates.map((d) => (
                    <button
                      key={fmtDate(d)}
                      type="button"
                      className={`date-card ${
                        selectedDate && fmtDate(selectedDate) === fmtDate(d) ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedDate(d);
                        setSelectedSlot(null);
                      }}
                    >
                      <span className="date-weekday">
                        {d.toLocaleDateString("en-US", { weekday: "short" })}
                      </span>
                      <span className="date-day">{d.getDate()}</span>
                      <span className="date-month">
                        {d.toLocaleDateString("en-US", { month: "short" })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedDate && (
                <div className="form-group">
                  <label>Pick a Time</label>
                  {slots.length === 0 ? (
                    <div className="no-slots">
                      We're closed for the rest of this day. Please pick another date.
                    </div>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          className={`slot-card ${selectedSlot === s.value ? "selected" : ""}`}
                          onClick={() => setSelectedSlot(s.value)}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="step-buttons">
                <button className="book-btn book-btn-secondary" onClick={() => setStep(1)}>
                  <i className="fas fa-arrow-left mr-2" />
                  Back
                </button>
                <button
                  className="book-btn book-btn-primary"
                  disabled={!selectedDate || !selectedSlot}
                  onClick={() => setStep(3)}
                >
                  Next: Your Info
                  <i className="fas fa-arrow-right ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: contact + confirm */}
          {step === 3 && (
            <form ref={formRef} onSubmit={handleSubmit}>
              {/* Booking summary */}
              <div className="booking-summary">
                <div className="summary-row">
                  <span>Device:</span>
                  <strong>{device}</strong>
                </div>
                <div className="summary-row">
                  <span>Issue:</span>
                  <strong>{issue}</strong>
                </div>
                <div className="summary-row">
                  <span>Date:</span>
                  <strong>{selectedDate && displayDate(selectedDate)}</strong>
                </div>
                <div className="summary-row">
                  <span>Time:</span>
                  <strong>
                    {slots.find((s) => s.value === selectedSlot)?.label || selectedSlot}
                  </strong>
                </div>
              </div>

              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="book-input"
                  placeholder="Full name"
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="book-input"
                  placeholder="(215) 555-0000"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="book-input"
                  placeholder="you@example.com"
                />
              </div>

              {status.message && (
                <div className={`book-status ${status.type}`}>
                  <i
                    className={`fas ${
                      status.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"
                    } mr-2`}
                  />
                  {status.message}
                </div>
              )}

              {status.type !== "success" && (
                <div className="step-buttons">
                  <button
                    type="button"
                    className="book-btn book-btn-secondary"
                    onClick={() => setStep(2)}
                  >
                    <i className="fas fa-arrow-left mr-2" />
                    Back
                  </button>
                  <button type="submit" className="book-btn book-btn-primary" disabled={submitting}>
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2" />
                        Booking…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-2" />
                        Confirm Booking
                      </>
                    )}
                  </button>
                </div>
              )}

              {status.type === "success" && (
                <div className="text-center" style={{ marginTop: "16px" }}>
                  <Link to="/" onClick={onClose} className="book-btn book-btn-primary" style={{ display: "inline-block" }}>
                    Done
                  </Link>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer note */}
        <div className="book-repair-footer">
          <i className="fas fa-shield-alt" /> 30-Day Warranty · Same-Day Service · Free Diagnostic
        </div>
      </div>
    </div>
  );
};

export default BookRepair;
